import { apiFetch } from "./client"
import type { SedeOut, UsuarioOut, UsuarioIn } from "@/types/api"
export type { SedeOut, UsuarioOut, UsuarioIn } from "@/types/api"

export interface SedeIn {
  nombre: string
  direccion?: string
  telefono?: string
  email?: string
  es_principal?: boolean
  activa?: boolean
  capacidad_maxima?: number
  horarios_atencion?: Record<string, { activo: boolean; hora_inicio: string; hora_fin: string }>
  dias_bloqueados?: Array<{ fecha: string; motivo: string }>
  profesionales_ids?: number[]
}

// New API structure for configuration module redesign
export interface ConfigGeneral {
  clinica_nombre: string
  clinica_direccion: string
  reserva_monto: string
  reserva_cbu: string
  reserva_alias: string
  zona_horaria: string
}

export interface ConfigConexiones {
  wa_phone_id: string
  wa_from_number: string
  brevo_from_email: string
  brevo_from_name: string
  // Multi-provider email fields
  email_provider: 'brevo' | 'smtp' | 'sendgrid'
  brevo_api_key_configured: boolean
  smtp_host: string
  smtp_port: number
  smtp_user: string
  smtp_use_tls: boolean
}

export interface ConfigPagos {
  mp_public_key_configured:     boolean
  mp_access_token_configured:   boolean
  mp_webhook_secret_configured: boolean
  mp_activo: boolean
  mp_modo:   'produccion' | 'sandbox'
  mp_comision_porcentaje: number
}

export interface ConfigPagosIn {
  mp_public_key?:          string
  mp_access_token?:        string
  mp_webhook_secret?:      string
  mp_activo?:              boolean
  mp_modo?:                'produccion' | 'sandbox'
  mp_comision_porcentaje?: number
}

export interface TrashItem {
  id: string
  model_name: string
  display_name: string
  deleted_at: string
  deleted_by: string | null
  can_restore: boolean
}

export interface PapeleraItem {
  dni: string
  nombre: string
  apellido: string
  fecha_eliminacion: string | null
}

export interface GlobalStats {
  empresas: number
  pacientes_activos: number
  pacientes_eliminados: number
  turnos_activos: number
  turnos_eliminados: number
  sedes: number
  usuarios_activos: number
  usuarios_inactivos: number
  presupuestos: number
  ordenes_laboratorio: number
  documentos_firmados: number
  procedimientos: number
  insumos: number
  mensajes_marketing: number
  gastos_fijos: number
  logs_actividad: number
}

export interface SistemaInfo {
  django_version: string
  python_version: string
  db_status: string
  total_pacientes: number
  total_turnos: number
  total_usuarios: number
  total_sedes: number
  entorno: string
  tenant_id: string
  tenant_slug: string
  tenant_nombre: string
  tenant_plan: string
  tenant_identificador_fiscal: string
  logs_recientes: Array<{
    id: string
    accion: string
    usuario: string
    fecha: string
    tabla: string
    metadata: Record<string, unknown>
  }>
  global_stats: GlobalStats | null
}

// Usuarios
export const listUsuarios    = ()                          => apiFetch<UsuarioOut[]>('/pacientes/config/usuarios/')
export const createUsuario   = (d: UsuarioIn)              => apiFetch<UsuarioOut>('/pacientes/config/usuarios/', { method: 'POST', body: JSON.stringify(d) })
export const updateUsuario   = (id: number, d: UsuarioIn)  => apiFetch<UsuarioOut>(`/pacientes/config/usuarios/${id}/`, { method: 'PUT', body: JSON.stringify(d) })
export const deleteUsuario   = (id: number)                => apiFetch<void>(`/pacientes/config/usuarios/${id}/`, { method: 'DELETE' })
export const resetPassword   = (id: number, nueva_password: string) => apiFetch<{success: boolean; message: string}>(`/pacientes/config/usuarios/${id}/reset-password/`, { method: 'POST', body: JSON.stringify({ nueva_password }) })
export const uploadUsuarioFoto = (id: number, file: File): Promise<{ foto_url: string }> => {
  const form = new FormData()
  form.append("foto", file)
  return apiFetch<{ foto_url: string }>(`/pacientes/config/usuarios/${id}/foto/`, { method: 'POST', body: form })
}

// Sedes — getConfigSedes es alias de listSedes, usar solo listSedes
export const listSedes   = ()                          => apiFetch<SedeOut[]>('/pacientes/config/sedes/')
export const createSede  = (d: SedeIn)                 => apiFetch<SedeOut>('/pacientes/config/sedes/', { method: 'POST', body: JSON.stringify(d) })
export const updateSede  = (id: string, d: SedeIn)     => apiFetch<SedeOut>(`/pacientes/config/sedes/${id}/`, { method: 'PUT', body: JSON.stringify(d) })
export const deleteSede  = (id: string)                => apiFetch<{success: boolean}>(`/pacientes/config/sedes/${id}/`, { method: 'DELETE' })

// New Config API endpoints
export const getConfigGeneral  = ()                    => apiFetch<ConfigGeneral>('/pacientes/config/general/')
export const saveConfigGeneral = (d: Partial<ConfigGeneral>) => apiFetch<{success: boolean}>('/pacientes/config/general/', { method: 'POST', body: JSON.stringify(d) })

export const getConfigConexiones  = ()                    => apiFetch<ConfigConexiones>('/pacientes/config/conexiones/')
export const saveConfigConexiones = (d: Partial<ConfigConexiones>) => apiFetch<{success: boolean}>('/pacientes/config/conexiones/', { method: 'POST', body: JSON.stringify(d) })
export const testConexiones = () => apiFetch<{success: boolean; message: string}>('/pacientes/config/conexiones/test/', { method: 'POST' })
export const testEmail = () => apiFetch<{success: boolean; message: string}>('/pacientes/config/test-email/', { method: 'POST' })

export const getConfigSedes  = listSedes
export const saveConfigSedes = (id: string, d: SedeIn) => apiFetch<SedeOut>(`/pacientes/config/sedes/${id}/`, { method: 'PUT', body: JSON.stringify(d) })

export const getConfigPagos  = ()                    => apiFetch<ConfigPagos>('/pacientes/config/pagos/')
export const saveConfigPagos = (d: ConfigPagosIn) => apiFetch<{success: boolean}>('/pacientes/config/pagos/', { method: 'POST', body: JSON.stringify(d) })
export const testConfigPagos = ()                 => apiFetch<{success: boolean; message: string}>('/pacientes/config/pagos/test/', { method: 'POST' })

// Papelera
export const listPapelera       = ()           => apiFetch<TrashItem[]>('/pacientes/config/papelera/')
export const restoreTrashItem   = (model: string, id: string) => apiFetch<{success: boolean; message: string}>(`/pacientes/config/papelera/restore/${model}/${id}/`, { method: 'POST' })
export const deleteTrashItem    = (model: string, id: string) => apiFetch<{success: boolean; message: string}>(`/pacientes/config/papelera/delete/${model}/${id}/`, { method: 'DELETE' })
export const emptyTrash         = (action: 'delete' | 'restore', confirm: boolean) => apiFetch<{success: boolean; message: string; count: number}>('/pacientes/config/papelera/empty/', { method: 'POST', body: JSON.stringify({ action, confirm }) })

// Sistema
export const getSistemaInfo = () => apiFetch<SistemaInfo>('/pacientes/config/sistema/')

// Empresas (superuser only)
export interface EmpresaOut {
  id: string
  nombre: string
  identificador_fiscal: string
  slug: string
  plan: string
  created_at: string
}

export interface EmpresaIn {
  nombre: string
  identificador_fiscal: string
  slug: string
  plan: string
}

export const listEmpresas   = ()                           => apiFetch<EmpresaOut[]>('/pacientes/config/empresas/')
export const updateEmpresa  = (id: string, d: EmpresaIn)   => apiFetch<EmpresaOut>(`/pacientes/config/empresas/${id}/`, { method: 'PUT', body: JSON.stringify(d) })
export const deleteEmpresa  = (id: string)                 => apiFetch<{success: boolean}>(`/pacientes/config/empresas/${id}/`, { method: 'DELETE' })
